import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Search } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const KeywordsManagement = () => {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newKeyword, setNewKeyword] = useState({
    keyword: '',
    weight: 5,
    category: 'standard',
    description: ''
  });
  const [filter, setFilter] = useState({ category: '', search: '' });

  useEffect(() => {
    fetchKeywords();
  }, []);

  const fetchKeywords = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/keywords`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKeywords(response.data);
    } catch (error) {
      console.error('Error fetching keywords:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/admin/keywords`, newKeyword, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewKeyword({ keyword: '', weight: 5, category: 'standard', description: '' });
      setShowAddForm(false);
      fetchKeywords();
    } catch (error) {
      console.error('Error adding keyword:', error);
      alert(error.response?.data?.error || 'Failed to add keyword');
    }
  };

  const handleEdit = (keyword) => {
    setEditingId(keyword.id);
    setEditForm({
      keyword: keyword.keyword,
      weight: keyword.weight,
      category: keyword.category,
      description: keyword.description || '',
      active: keyword.active
    });
  };

  const handleSave = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/admin/keywords/${id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingId(null);
      fetchKeywords();
    } catch (error) {
      console.error('Error updating keyword:', error);
      alert('Failed to update keyword');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this keyword?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/keywords/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchKeywords();
    } catch (error) {
      console.error('Error deleting keyword:', error);
      alert('Failed to delete keyword');
    }
  };

  const handleToggleActive = async (id, currentActive) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/admin/keywords/${id}`, 
        { active: !currentActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchKeywords();
    } catch (error) {
      console.error('Error toggling keyword:', error);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      life_threatening: 'bg-red-100 text-red-800',
      urgent: 'bg-orange-100 text-orange-800',
      important: 'bg-yellow-100 text-yellow-800',
      standard: 'bg-blue-100 text-blue-800',
      administrative: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.standard;
  };

  const getWeightColor = (weight) => {
    if (weight >= 9) return 'text-red-600 font-bold';
    if (weight >= 7) return 'text-orange-600 font-semibold';
    if (weight >= 5) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const filteredKeywords = keywords.filter(k => {
    if (filter.category && k.category !== filter.category) return false;
    if (filter.search && !k.keyword.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return <div className="text-center py-8">Loading keywords...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Clinical Keywords Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure importance scoring weights for AI priority calculation
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          Add Keyword
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Add New Keyword</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Keyword</label>
              <input
                type="text"
                value={newKeyword.keyword}
                onChange={(e) => setNewKeyword({ ...newKeyword, keyword: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., chest pain"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weight (0-10)</label>
              <input
                type="number"
                min="0"
                max="10"
                value={newKeyword.weight}
                onChange={(e) => setNewKeyword({ ...newKeyword, weight: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={newKeyword.category}
                onChange={(e) => setNewKeyword({ ...newKeyword, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="life_threatening">Life Threatening</option>
                <option value="urgent">Urgent</option>
                <option value="important">Important</option>
                <option value="standard">Standard</option>
                <option value="administrative">Administrative</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={newKeyword.description}
                onChange={(e) => setNewKeyword({ ...newKeyword, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add Keyword
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search keywords..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filter.category}
            onChange={(e) => setFilter({ ...filter, category: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            <option value="life_threatening">Life Threatening</option>
            <option value="urgent">Urgent</option>
            <option value="important">Important</option>
            <option value="standard">Standard</option>
            <option value="administrative">Administrative</option>
          </select>
        </div>
      </div>

      {/* Keywords Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Keyword
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Weight
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredKeywords.map((keyword) => (
              <tr key={keyword.id} className={!keyword.active ? 'opacity-50' : ''}>
                {editingId === keyword.id ? (
                  <>
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        value={editForm.keyword}
                        onChange={(e) => setEditForm({ ...editForm, keyword: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={editForm.weight}
                        onChange={(e) => setEditForm({ ...editForm, weight: parseInt(e.target.value) })}
                        className="w-20 px-2 py-1 border border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={editForm.category}
                        onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded"
                      >
                        <option value="life_threatening">Life Threatening</option>
                        <option value="urgent">Urgent</option>
                        <option value="important">Important</option>
                        <option value="standard">Standard</option>
                        <option value="administrative">Administrative</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.active}
                          onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                          className="mr-2"
                        />
                        Active
                      </label>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => handleSave(keyword.id)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <Save className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {keyword.keyword}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-2xl ${getWeightColor(keyword.weight)}`}>
                        {keyword.weight}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(keyword.category)}`}>
                        {keyword.category.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {keyword.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(keyword.id, keyword.active)}
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          keyword.active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {keyword.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(keyword)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(keyword.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {filteredKeywords.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No keywords found
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Total Keywords:</strong> {keywords.length} 
          {' | '}
          <strong>Active:</strong> {keywords.filter(k => k.active).length}
          {' | '}
          <strong>Showing:</strong> {filteredKeywords.length}
        </p>
        <p className="text-xs text-blue-600 mt-2">
          💡 Changes take effect within 5 minutes (cache refresh interval)
        </p>
      </div>
    </div>
  );
};

export default KeywordsManagement;
